// ────────────────────────────────────────────────────────────────
// core/layout/shell.ts — the authenticated app shell (§6.1)
// Left nav (capability-gated), top bar with the env switcher (confirmed
// on change, persisted per admin) + admin menu, and the persistent red
// PRODUCTION banner while env = prd.
// ────────────────────────────────────────────────────────────────

import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink, RouterLinkActive, RouterOutlet, Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { MenuModule } from 'primeng/menu';
import { Tooltip } from 'primeng/tooltip';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { SessionStore } from '../auth/session.store';
import { EnvStore } from '../env/env.store';
import { AuthService } from '../auth/auth.service';
import { Capability } from '../models/admin.model';
import { ENVS, ENV_LABELS, Env } from '../models/env.model';
import { helpSlugForUrl } from '../../help/help-content';
import { NavMemoryService } from '../nav/nav-memory.service';

interface NavItem {
  label: string;
  icon: string;
  route: string;
  capabilities: Capability[];
}

const NAV_ITEMS: NavItem[] = [
  { label: 'Exceptions', icon: 'pi pi-exclamation-triangle', route: '/exceptions', capabilities: ['exceptions.read'] },
  { label: 'Health', icon: 'pi pi-heart-fill', route: '/health', capabilities: ['health.read'] },
  { label: 'Maintenance', icon: 'pi pi-wrench', route: '/maintenance', capabilities: ['maintenance.read'] },
  { label: 'Promotions', icon: 'pi pi-tag', route: '/promotions', capabilities: ['coupons.read'] },
  { label: 'Customer 360', icon: 'pi pi-id-card', route: '/c360', capabilities: ['c360.read'] },
  { label: 'Catalog', icon: 'pi pi-th-large', route: '/catalog', capabilities: ['catalog.read'] },
  { label: 'Feedback', icon: 'pi pi-comments', route: '/browsers/feedbacks', capabilities: ['feedbacks.read'] },
  { label: 'Logs', icon: 'pi pi-list-check', route: '/browsers/logs', capabilities: ['logs.read'] },
  { label: 'Commissions', icon: 'pi pi-percentage', route: '/commissions', capabilities: ['commissions.read'] },
  { label: 'Reference data', icon: 'pi pi-book', route: '/reference-data', capabilities: ['reference-data.read'] },
  { label: 'Analytics', icon: 'pi pi-chart-bar', route: '/analytics', capabilities: ['analytics.read'] },
  { label: 'Audit', icon: 'pi pi-history', route: '/audit', capabilities: ['audit.read'] },
  { label: 'Settings', icon: 'pi pi-cog', route: '/settings', capabilities: ['config.read'] },
  { label: 'Help', icon: 'pi pi-question-circle', route: '/help', capabilities: [] },
];

@Component({
  selector: 'app-shell',
  imports: [
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    FormsModule,
    SelectModule,
    ButtonModule,
    MenuModule,
    Tooltip,
    ConfirmDialog,
  ],
  providers: [ConfirmationService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './shell.html',
  styleUrl: './shell.scss',
})
export class Shell {
  private readonly session = inject(SessionStore);
  private readonly envStore = inject(EnvStore);
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly confirm = inject(ConfirmationService);
  readonly navMemory = inject(NavMemoryService);

  readonly admin = this.session.admin;
  readonly env = this.envStore.env;
  readonly isPrd = this.envStore.isPrd;

  // Context help: the "?" in the top bar deep-links to the active screen's help
  // page (§6.2 #11 — "every feature screen has a ? linking to its page").
  private readonly currentUrl = signal(this.router.url);
  readonly helpRoute = computed(() => ['/help', helpSlugForUrl(this.currentUrl())]);

  // Only show nav entries the admin can actually use.
  readonly navItems = computed(() => NAV_ITEMS.filter((i) => this.session.hasAnyCapability(i.capabilities)));

  // Env switcher options (label + code, never a naked code).
  readonly envOptions = ENVS.map((e) => ({ label: `${ENV_LABELS[e]} (${e})`, value: e }));

  constructor() {
    // Track the active URL so the context-help "?" points at the right page,
    // and remember it under whichever nav section it belongs to (§ deep-link
    // memory — the left nav then reopens that section where it was left).
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed()
      )
      .subscribe((e) => {
        this.currentUrl.set(e.urlAfterRedirects);
        const path = e.urlAfterRedirects.split('?')[0];
        const item = NAV_ITEMS.find((i) => path === i.route || path.startsWith(`${i.route}/`));
        if (item) this.navMemory.record(item.route, e.urlAfterRedirects);
      });
  }

  readonly adminMenu = computed<MenuItem[]>(() => [
    {
      label: this.admin()?.email ?? '',
      items: [
        { label: 'Authenticator app', icon: 'pi pi-mobile', command: () => this.router.navigate(['/account/totp']) },
        { separator: true },
        { label: 'Sign out', icon: 'pi pi-sign-out', command: () => this.auth.logout() },
      ],
    },
  ]);

  // Confirm before switching env (clears feature stores + persists — §6.1).
  onEnvChange(target: Env): void {
    if (target === this.envStore.env()) return;
    this.confirm.confirm({
      header: 'Switch environment',
      message: `Switch to ${ENV_LABELS[target]} (${target})? Any in-progress feature state will be cleared.`,
      icon: 'pi pi-arrow-right-arrow-left',
      acceptLabel: `Switch to ${target}`,
      rejectLabel: 'Stay',
      // The <p-select> is bound one-way to env(); it only moves once we
      // actually switch here, so a cancel leaves the control on the
      // current env with no extra work.
      accept: () => this.envStore.setEnv(target),
    });
  }
}
